/**
 * Cookie utility.
 * Provides methods for setting, getting, and managing cookies.
 *
 */

/**
 * Retrieves a cookie value by its name.
 * Logic is on a script tag 'scripts.utils-cookies.liquid' to also be available for other scripts
 * Error handling is done in the script tag
 *
 * @param {string} name - The name of the cookie.
 * @returns {string|null} The cookie value or null if not found.
 */
const getCookie = (name) => window.ALLBIRDS.Cookies.getCookie(name);

/**
 * Sets a cookie with the given name, value, and options.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {import('~/types/cookies').CookieOptions} [options={}] - Cookie options.
 */
const setCookie = (name, value, options = {}) => {
  const { days, domain, maxAge, path = '/', sameSite, secure } = options;
  const cookieParts = [`${name}=${encodeURIComponent(value)}`];

  // Handle expiration - prefer maxAge if provided, otherwise use days.
  if (maxAge !== undefined) {
    cookieParts.push(`max-age=${maxAge}`);
  } else if (days !== undefined) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    cookieParts.push(`expires=${date.toUTCString()}`);
  }

  cookieParts.push(`path=${path}`);

  if (domain) {
    cookieParts.push(`domain=${domain}`);
  }

  if (secure) {
    cookieParts.push('secure');
  }

  if (sameSite) {
    cookieParts.push(`samesite=${sameSite}`);
  }

  document.cookie = cookieParts.join(';');
};

/**
 * Removes a cookie by setting its expiration date to the past.
 *
 * @param {string} name - The name of the cookie to remove.
 * @param {import('~/types/cookies').CookieOptions} [options={}] - Cookie options such as path and domain.
 */
const removeCookie = (name, options = {}) => {
  const { domain, path = '/' } = options;
  setCookie(name, '', { days: -1, domain, path });
};

/**
 * Sets multiple cookies at once.
 *
 * @param {Array<import('~/types/cookies').CookieObject>} cookies - An array of cookie objects.
 * Each object should have a `name`, `value`, and optionally additional cookie options.
 */
const setCookies = (cookies) => {
  if (!Array.isArray(cookies)) return;
  cookies.forEach((cookie) => {
    if (cookie.name && cookie.value !== undefined) {
      setCookie(cookie.name, cookie.value, cookie);
    }
  });
};

export { getCookie, removeCookie, setCookie, setCookies };
